import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import { RenderType } from "../../../../Enums/Enums";
import { clamp } from "../../../../Util/DataUtil";
import { TemplateImageGeneratorProgressStep } from "../../../../Util/TemplateImageGenerator";

const INITIALIZATION_DURATION_SECONDS = 1;
const GOOGLE_MAP_WAIT_DURATION_SECONDS = 7.5;
const GOOGLE_MAP_RENDER_DURATION_SECONDS = 7.5;
const OUTPUT_RENDER_DURATION_SECONDS = 2.5;
const ZIP_DURATION_SECONDS = 2;
const BUILD_PDF_PER_OUTPUT_DURATION_SECONDS = 1.14;
const BUILD_PDF_OVERHEAD_DURATION_SECONDS = 1.14;

/* Calculations for Build PDF duration
    Mobilytics: 24 sec, 20 outputs
    Match: 16 sec, 13 outputs

    output is probably constant between apps
    output: x
    server send/receive/PDF overhead: y

    20x + y = 24
    13x + y = 16
    y = 16 - 13x
    20x + 16 - 13x = 24
    7x = 8
    x = 8/7
    y = 16 - 13*8/7
    x = 1.14
    y = 16 - 14.86
    y = 1.14
*/

export enum ExtraProgressStep {
    Initializing = "Initializing",
    Zipping = "Zipping",
    BuildingPDF = "BuildingPDF",
    DownloadComplete = "DownloadComplete",
}

export interface IDigitalShareProgress {
    succeeded: number;
    failed: number;
    total: number;
}

export type DigitalShareProgressStep =
    | TemplateImageGeneratorProgressStep
    | ExtraProgressStep;

export const getProgressDisplayDetails = (
    selectedOutputs: DigitalShareItemType[],
    progressStep: DigitalShareProgressStep,
    generationProgress: IDigitalShareProgress,
    renderType: RenderType
): { text: string; percentComplete: number } => {
    let googleMaps = 0;
    const outputs = selectedOutputs.length;
    for (const o of selectedOutputs) {
        // if (o === DigitalShareItemType.VisitorMap) {

        // }

        switch (o) {
            case DigitalShareItemType.VisitorMap:
                ++googleMaps;
            case DigitalShareItemType.FrequentlyVisitedPlacesMap:
                ++googleMaps;
            case DigitalShareItemType.BrandComparison:
                ++googleMaps;

            default:
                break;
        }
    }

    let endPhase = 0;
    if (renderType === RenderType.DIGITAL_SHARE) {
        endPhase = ZIP_DURATION_SECONDS;
    } else {
        endPhase
            = BUILD_PDF_OVERHEAD_DURATION_SECONDS
            + outputs * BUILD_PDF_PER_OUTPUT_DURATION_SECONDS;
    }

    const total
        = INITIALIZATION_DURATION_SECONDS
        + googleMaps * GOOGLE_MAP_WAIT_DURATION_SECONDS
        + googleMaps * GOOGLE_MAP_RENDER_DURATION_SECONDS
        + outputs * OUTPUT_RENDER_DURATION_SECONDS
        + endPhase;

    let complete = 0;
    let percentComplete = 0;

    let text = "Initializing...";
    if (googleMaps > 1) {
        text = "Taking snapshot of maps...";
    } else if (googleMaps === 1) {
        text = "Taking snapshot of map...";
        complete = INITIALIZATION_DURATION_SECONDS;
    }

    if (
        progressStep === TemplateImageGeneratorProgressStep.WaitingForComponents
    ) {
        text = "Waiting for components...";
        complete
            = INITIALIZATION_DURATION_SECONDS
            + googleMaps * GOOGLE_MAP_WAIT_DURATION_SECONDS;
    } else if (
        progressStep === TemplateImageGeneratorProgressStep.RenderingOutputs
    ) {
        complete
            = INITIALIZATION_DURATION_SECONDS
            + googleMaps * GOOGLE_MAP_WAIT_DURATION_SECONDS
            + googleMaps * GOOGLE_MAP_RENDER_DURATION_SECONDS
            + (generationProgress.succeeded - googleMaps)
            * OUTPUT_RENDER_DURATION_SECONDS;

        const failedMessage = generationProgress.failed
            ? `(${generationProgress.failed} failed)`
            : "";
        text = `Creating output for ${Math.min(
            generationProgress.succeeded + 1,
            generationProgress.total
        )} of ${generationProgress.total} ${failedMessage}`;
    } else if (progressStep === ExtraProgressStep.Zipping) {
        text = "Zipping...";
        complete
            = INITIALIZATION_DURATION_SECONDS
            + googleMaps * GOOGLE_MAP_WAIT_DURATION_SECONDS
            + googleMaps * GOOGLE_MAP_RENDER_DURATION_SECONDS
            + (generationProgress.succeeded - googleMaps)
            * OUTPUT_RENDER_DURATION_SECONDS;
    } else if (progressStep === ExtraProgressStep.BuildingPDF) {
        text = "Building PDF...";
        complete
            = INITIALIZATION_DURATION_SECONDS
            + googleMaps * GOOGLE_MAP_WAIT_DURATION_SECONDS
            + googleMaps * GOOGLE_MAP_RENDER_DURATION_SECONDS
            + (generationProgress.succeeded - googleMaps)
            * OUTPUT_RENDER_DURATION_SECONDS;
    } else if (progressStep === ExtraProgressStep.DownloadComplete) {
        text = "Download complete!";
        complete = total;
    }

    percentComplete = (complete / total) * 100.0;
    percentComplete = clamp(percentComplete, 0, 100);

    return {
        text,
        percentComplete,
    };
};
